import { isFlashHotelType } from 'lib/offer/offerTypes'

export function getTotalChildren(filters?: App.OfferListFilters): number {
  return filters?.rooms?.reduce((sum, room) => sum + (room.children ?? 0), 0) ?? 0
}

export function getTotalInfants(filters?: App.OfferListFilters): number {
  return filters?.rooms?.reduce((sum, room) => sum + (room.infants ?? 0), 0) ?? 0
}

/**
 * Determines whether children or infants are genuinely free for a single room
 * rate by checking if they appear alongside the maximum adult capacity.
 *
 * When the max adult capacity entry also includes children or infants, those
 * guests are extra — truly free on top of the room's full adult occupancy.
 * When children only appear at lower adult counts they are filling adult slots,
 * not staying for free.
 */
export function getFreeGuestTypesAtMaxOccupancy(
  capacities: Array<App.RoomCapacity>,
): { childrenFree: boolean; infantsFree: boolean } {
  if (capacities.length === 0) return { childrenFree: false, infantsFree: false }

  const maxAdults = Math.max(...capacities.map(c => c.adults))
  const atMaxOccupancy = capacities.filter(c => c.adults === maxAdults)

  return {
    childrenFree: atMaxOccupancy.some(c => c.children > 0),
    infantsFree: atMaxOccupancy.some(c => c.infants > 0),
  }
}

/**
 * Returns the number of children/infants that stay free based on room rate
 * capacities across all packages.
 *
 * Each inner array represents one room rate's `capacities`. We check each
 * rate independently: find its max adult capacity, then see if children or
 * infants also appear at that level. This avoids false positives for
 * large-capacity rooms where children simply fill adult slots.
 *
 * Returns 0 when the offer is not a flash hotel type, no guests are
 * genuinely free, or no children/infants are in the search filters.
 */
export function getFlashFreeChildCount({
  offerType,
  filters,
  capacitiesByRate,
}: {
  offerType: App.OfferType
  filters?: App.OfferListFilters
  capacitiesByRate: Array<Array<App.RoomCapacity>>
}): number {
  if (!isFlashHotelType(offerType)) return 0

  let childrenFree = false
  let infantsFree = false

  for (const capacities of capacitiesByRate) {
    const result = getFreeGuestTypesAtMaxOccupancy(capacities)
    if (result.childrenFree) childrenFree = true
    if (result.infantsFree) infantsFree = true
  }

  if (!childrenFree && !infantsFree) return 0

  let totalFreeCount = 0
  if (childrenFree) totalFreeCount += getTotalChildren(filters)
  if (infantsFree) totalFreeCount += getTotalInfants(filters)

  return totalFreeCount
}
